import React from 'react';

import {
  Box,
  FormControlLabel,
  styled as MuiStyled,
  Radio,
  RadioGroup,
  Stack,
  TextareaAutosize,
  Typography,
} from '@mui/material';

import styled from '@emotion/styled';
import { capitalize } from 'lodash';

import { ComplaintItemInput } from '../../../graphql/API';
import Button from '../../Button/Button';
import LoadingButton from '../../Button/LoadingButton';
import BaseDialog, { BaseDialogProps } from '../Base';

type Props = {
  isPrivateOffer?: boolean;
  onSubmitQuestions?: (questions: ComplaintItemInput[]) => void;
} & BaseDialogProps;

type RadioType = { title: string; value: string };

const RoundRadio = MuiStyled('div', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  minWidth: '75px',
  height: '70px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '10px',
  border: `1.2px solid ${selected ? '#000' : '#E6E6E6'}`,
  backgroundColor: selected ? '#000' : '#fff',
  color: selected ? '#fff' : '#000',
  cursor: 'pointer',
  margin: 0,
}));

const TypographyRadio = MuiStyled('div', {
  shouldForwardProp: (prop) => prop !== 'selected',
})<{ selected: boolean }>(({ selected }) => ({
  padding: '10px 32px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  border: `1px solid ${selected ? '#000' : '#E6E6E6'}`,
  backgroundColor: selected ? '#000' : '#fff',
  color: selected ? '#fff' : '#000',
  cursor: 'pointer',
  fontSize: '15px',
}));

const QUALITY_STANDARDS = [1, 2, 3, 4, 5];
const CAMPAIGN_REQUIREMENTS: RadioType[] = [
  { title: 'yes', value: '1' },
  { title: 'no', value: '2' },
  { title: 'partialy', value: '3' },
];
const CAMPAIGN_SCHEDULING: RadioType[] = [
  { title: 'yes', value: '1' },
  { title: 'no', value: '2' },
];

const EXPECTED_NUMBER_OF_BOOKINGS: RadioType[] = [
  {
    title: 'Yes, but the models weren’t the right fit.',
    value: '1',
  },
  { title: 'No, fewer models booked than expected.', value: '2' },
  { title: 'Yes, it met my expectations.', value: '3' },
];

const CANCELLING_REASONS: RadioType[] = [
  { title: 'The content produced didn’t meet my expectations.', value: '1' },
  { title: 'Model’s follower count was too low.', value: '2' },
  { title: 'The timing or frequency of the posts didn’t align with my needs..', value: '3' },
  { title: 'The model didn’t match my target audience.', value: '4' },
  { title: 'The offer setup process was too complicated.', value: '5' },
  { title: 'Other (please specify)', value: '6' },
];

type Questions = 'Q1' | 'Q2' | 'Q3' | 'Q4' | 'Q5' | 'Q6' | 'Q7';

const QUESTIONS: Record<Questions, RadioType> = {
  Q1: {
    title: 'How would you rate the quality of posts you received?',
    value: 'Q1',
  },
  Q2: {
    title: 'Did the selected models meet your campaign requirements?',
    value: 'Q2',
  },
  Q3: {
    title: 'Was scheduling a problem for your campaign?',
    value: 'Q3',
  },
  Q4: {
    title: 'Did the offer achieve your expected number of bookings?',
    value: 'Q4',
  },
  Q5: {
    title: 'Please select the main reason for canceling your offer',
    value: 'Q5',
  },
  Q6: {
    title: 'How would you rate the ease of setting up and managing your recurring offer?',
    value: 'Q6',
  },
  Q7: {
    title: 'How would you rate the process of selecting and inviting models for your collab offer?',
    value: 'Q7',
  },
};

export const OfferSurveyDialog: React.FC<Props> = ({
  isPrivateOffer = false,
  onSubmitQuestions,
  ...rest
}) => {
  const [answers, setAnswers] = React.useState<Record<Questions, string>>({
    Q1: '',
    Q2: '',
    Q3: '',
    Q4: '',
    Q5: '',
    Q6: '',
    Q7: '',
  });

  const [elaborateRating, setElaborateRating] = React.useState('');
  const [questionFiveSpecify, setQuestionFiveSpecify] = React.useState('');

  const handleChange = React.useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    const name = e.currentTarget.name as Questions;
    const v = e.currentTarget.value as string;
    setAnswers((prev) => ({
      ...prev,
      [name]: v,
    }));
  }, []);

  const handleElaborateRating = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setElaborateRating((event.target as HTMLTextAreaElement).value);
  };

  const handleFiveSpecifyQuestion = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setQuestionFiveSpecify((event.target as HTMLTextAreaElement).value);
  };

  const handleSubmit = () => {
    const questions: ComplaintItemInput[] = [];

    questions.push({ title: QUESTIONS.Q1.title, name: 'Question#1', value: answers.Q1 });
    questions.push({
      title: QUESTIONS.Q2.title,
      name: 'Question#2',
      value: CAMPAIGN_REQUIREMENTS.find((campaingn) => campaingn.value === answers.Q2)?.title || '',
    });
    questions.push({
      title: QUESTIONS.Q3.title,
      name: 'Question#3',
      value: CAMPAIGN_SCHEDULING.find((campaingn) => campaingn.value === answers.Q3)?.title || '',
    });
    questions.push({
      title: QUESTIONS.Q4.title,
      name: 'Question#4',
      value:
        EXPECTED_NUMBER_OF_BOOKINGS.find((campaingn) => campaingn.value === answers.Q4)?.title ||
        '',
    });
    questions.push({
      title: QUESTIONS.Q5.title,
      name: 'Question#5',
      value:
        answers.Q5 === '6'
          ? questionFiveSpecify
          : CANCELLING_REASONS.find((campaingn) => campaingn.value === answers.Q5)?.title || '',
    });
    if (answers.Q6) {
      questions.push({
        title: QUESTIONS.Q6.title,
        name: 'Question#6',
        value: answers.Q6,
      });
    }
    if (answers.Q7) {
      questions.push({
        title: QUESTIONS.Q7.title,
        name: 'Question#7',
        value: answers.Q7,
      });
    }

    if (elaborateRating) {
      questions.push({
        title: 'Elaborate on your rating',
        name: 'Question#8',
        value: elaborateRating,
      });
    }

    if (onSubmitQuestions) onSubmitQuestions(questions);
  };

  const requiredFields: Questions[] = ['Q1', 'Q2', 'Q3', 'Q4', 'Q5'];
  if (!isPrivateOffer) requiredFields.push('Q6');
  if (isPrivateOffer) requiredFields.push('Q7');

  // Check if all required fields are filled
  const isButtonDisabled = requiredFields.some((key) => answers[key].trim() === '');

  return (
    <BaseDialog
      width='480px'
      minWidth='480px'
      height='900px'
      titleComponent={<ModalTitle>Feedback</ModalTitle>}
      footerComponent={
        <Box
          flex={1}
          display={'flex'}
          alignItems={'center'}
          gap={2}
          py={2}
          pl={1.8}
          pr={2.4}
          sx={{ borderTop: '1px solid #F2EFEF' }}
        >
          <Button
            variant={'outlined'}
            sx={{ padding: '15px 0', flex: 1 }}
            onClick={() => {
              if (rest?.onClose) rest?.onClose({}, 'escapeKeyDown');
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            fullWidth
            variant='contained'
            sx={{ padding: '15px 0', flex: 1.5 }}
            disabled={isButtonDisabled || (answers.Q5 === '6' && !questionFiveSpecify)}
            onClick={handleSubmit}
          >
            Submit
          </LoadingButton>
        </Box>
      }
      {...rest}
    >
      <Box maxWidth={'100%'} pr={0.7}>
        <Stack spacing={7}>
          {/* Q#1 */}
          <Box display={'flex'} flexDirection={'column'} gap={1.5}>
            <Typography m={0} p={0} fontSize={'15px'}>
              {QUESTIONS.Q1.title}
            </Typography>
            <RadioGroup
              row
              name='Q1'
              value={answers.Q1}
              onChange={handleChange}
              sx={{ gap: '8px' }}
            >
              {QUALITY_STANDARDS.map((value) => (
                <FormControlLabel
                  sx={{ flex: 1, margin: 0 }}
                  key={value}
                  value={value.toString()}
                  control={
                    <Radio
                      sx={{ display: 'none' }} // Hide default radio button
                    />
                  }
                  label={
                    <RoundRadio selected={answers.Q1 === value.toString()}>
                      <RoundRadioTitle selected={answers.Q1 === value.toString()}>
                        {value}
                      </RoundRadioTitle>
                    </RoundRadio>
                  }
                />
              ))}
            </RadioGroup>
            <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
              <Typography color={'#D04040'} fontSize={'12px'}>
                Very Poor
              </Typography>
              <Typography color={'#37BB90'} fontSize={'12px'}>
                Excellent
              </Typography>
            </Box>
          </Box>
          {/* Q#2 */}
          <Box display={'flex'} flexDirection={'column'} gap={1.5}>
            <Typography m={0} p={0} fontSize={'15px'}>
              {QUESTIONS.Q2.title}
            </Typography>
            <RadioGroup
              row
              name='Q2'
              value={answers.Q2}
              onChange={handleChange}
              sx={{ gap: '14px' }}
            >
              {CAMPAIGN_REQUIREMENTS.map(({ title, value }) => (
                <FormControlLabel
                  sx={{ margin: 0 }}
                  key={value}
                  value={value.toString()}
                  control={
                    <Radio
                      sx={{ display: 'none' }} // Hide default radio button
                    />
                  }
                  label={
                    <TypographyRadio selected={answers.Q2 === value.toString()}>
                      {capitalize(title)}
                    </TypographyRadio>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
          {/* Q#3 */}
          <Box display={'flex'} flexDirection={'column'} gap={1.5}>
            <Typography m={0} p={0} fontSize={'15px'}>
              {QUESTIONS.Q3.title}
            </Typography>
            <RadioGroup
              row
              name='Q3'
              value={answers.Q3}
              onChange={handleChange}
              sx={{ gap: '14px' }}
            >
              {CAMPAIGN_SCHEDULING.map(({ title, value }) => (
                <FormControlLabel
                  sx={{ margin: 0 }}
                  key={value}
                  value={value.toString()}
                  control={
                    <Radio
                      sx={{ display: 'none' }} // Hide default radio button
                    />
                  }
                  label={
                    <TypographyRadio selected={answers.Q3 === value.toString()}>
                      {capitalize(title)}
                    </TypographyRadio>
                  }
                />
              ))}
            </RadioGroup>
          </Box>
          {/* Q#4 */}
          <Box display={'flex'} flexDirection={'column'} gap={1.5}>
            <Typography m={0} p={0}>
              {QUESTIONS.Q4.title}
            </Typography>
            <RadioGroup
              row
              name='Q4'
              value={answers.Q4}
              onChange={handleChange}
              sx={{ gap: '12px' }}
            >
              {EXPECTED_NUMBER_OF_BOOKINGS.map(({ title, value }) => (
                <FormControlLabel
                  key={value}
                  value={value.toString()}
                  control={
                    <Radio
                      sx={{
                        color: '#E6E6E6',
                        '&.Mui-checked': {
                          color: '#000',
                        },
                      }}
                    />
                  }
                  label={<Typography fontSize={'15px'}>{title}</Typography>}
                  sx={{
                    borderRadius: '8px',
                    border:
                      answers.Q4 === value.toString() ? '1.5px solid #111' : '1px solid #E6E6E6',
                    width: '100%',
                    padding: '8px 4px',
                    margin: 0,
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
          {/* Q#5 */}
          <Box display={'flex'} flexDirection={'column'} gap={1.5}>
            <Typography m={0} p={0} fontSize={'15px'}>
              {QUESTIONS.Q5.title}
            </Typography>
            <RadioGroup
              row
              name='Q5'
              value={answers.Q5}
              onChange={handleChange}
              sx={{ gap: '12px' }}
            >
              {CANCELLING_REASONS.map(({ title, value }) => (
                <FormControlLabel
                  key={title}
                  value={value.toString()}
                  control={
                    <Radio
                      sx={{
                        color: '#E6E6E6',
                        '&.Mui-checked': {
                          color: '#000',
                        },
                      }}
                    />
                  }
                  label={<Typography fontSize={'15px'}>{title}</Typography>}
                  sx={{
                    borderRadius: '8px',
                    border:
                      answers.Q5 === value.toString() ? '1.5px solid #111' : '1px solid #E6E6E6',
                    width: '100%',
                    padding: '8px 4px',
                    margin: 0,
                  }}
                />
              ))}
            </RadioGroup>
            {answers.Q5 === '6' && (
              <TextareaAutosize
                value={questionFiveSpecify}
                minRows={6}
                style={{
                  borderRadius: '5px',
                  border: '1px solid #E6E6E6',
                  padding: '12px 20px',
                  resize: 'none',
                }}
                placeholder='Please specify'
                onChange={handleFiveSpecifyQuestion}
              />
            )}
          </Box>
          {/* Q#6 */}
          {/* Q#7 */}
          {!isPrivateOffer ? (
            <Box display={'flex'} flexDirection={'column'} gap={1.5}>
              <Typography m={0} fontSize={'15px'}>
                {QUESTIONS.Q6.title}
              </Typography>
              <RadioGroup
                row
                name='Q6'
                value={answers.Q6}
                onChange={handleChange}
                sx={{ gap: '8px' }}
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <FormControlLabel
                    sx={{ flex: 1, margin: 0 }}
                    key={value}
                    value={value.toString()}
                    control={
                      <Radio
                        sx={{ display: 'none' }} // Hide default radio button
                      />
                    }
                    label={
                      <RoundRadio selected={answers.Q6 === value.toString()}>
                        <RoundRadioTitle selected={answers.Q6 === value.toString()}>
                          {value}
                        </RoundRadioTitle>
                      </RoundRadio>
                    }
                  />
                ))}
              </RadioGroup>
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography color={'#D04040'} fontSize={'12px'}>
                  Very Difficult
                </Typography>

                <Typography color={'#37BB90'} fontSize={'12px'}>
                  Very Easy
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box display={'flex'} flexDirection={'column'} gap={1.5}>
              <Typography m={0} fontSize={'15px'}>
                {QUESTIONS.Q7.title}
              </Typography>
              <RadioGroup
                row
                name='Q7'
                value={answers.Q7}
                onChange={handleChange}
                sx={{ gap: '8px' }}
              >
                {QUALITY_STANDARDS.map((value) => (
                  <FormControlLabel
                    sx={{ flex: 1, margin: 0 }}
                    key={value}
                    value={value.toString()}
                    control={
                      <Radio
                        sx={{ display: 'none' }} // Hide default radio button
                      />
                    }
                    label={
                      <RoundRadio selected={answers.Q7 === value.toString()}>
                        <RoundRadioTitle selected={answers.Q7 === value.toString()}>
                          {value}
                        </RoundRadioTitle>
                      </RoundRadio>
                    }
                  />
                ))}
              </RadioGroup>
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <Typography color={'#D04040'} fontSize={'12px'}>
                  Very Difficult
                </Typography>
                <Typography color={'#37BB90'} fontSize={'12px'}>
                  Very Easy
                </Typography>
              </Box>
            </Box>
          )}
          <Box display={'flex'} flexDirection={'column'} gap={1.5}>
            <Typography fontSize={'17px'}>
              Elaborate on your rating{' '}
              <Typography component={'span'} color='#999'>
                (optional)
              </Typography>
            </Typography>
            <TextareaAutosize
              value={elaborateRating}
              minRows={6}
              style={{
                borderRadius: '5px',
                border: '1px solid #E6E6E6',
                padding: '12px 20px',
                resize: 'none',
              }}
              placeholder='Please elaborate on your rating'
              onChange={handleElaborateRating}
            />
          </Box>
        </Stack>
      </Box>
    </BaseDialog>
  );
};

const ModalTitle = styled.span`
  color: #000;
  font-size: 18px;
  font-weight: 500;
`;

const RoundRadioTitle = styled.span<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 2px solid ${(props) => (props.selected ? '#fff' : '#000')};
  border-radius: 50%;
  font-size: 15px;
  font-weight: 600;
  color: ${(props) => (props.selected ? '#fff' : '#000')};
`;
